import L from "leaflet";
import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import { DatosMonitoreo } from "../../../../../modulos/monitoreo/sesion/agruparPorSemana";
import { formatNivel } from "../../../../../modulos/monitoreo/sesion/formatIncidencia";
import {
  evaluarEnLote,
  MonitoreoEstado,
  SeleccionPlagas,
} from "../../../../../modulos/monitoreo/sesion/semaforo";
import { ESTADO_COLOR } from "../dashboard/monitoreoAnalisisPaleta";

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let BAR_ID = 0;

/** Barra de plagas del panel superior del mapa.
 *
 *  Un disparador compacto que despliega la lista de plagas, agrupadas por su
 *  `grupo` (Ácaro | Insecto | Hongo | Crítico). El punto de cada fila lleva el
 *  **peor estado de esa plaga en toda la semana**, así se ve de un vistazo
 *  dónde hay problema sin tener que ir plaga por plaga; al hacer clic, esa
 *  plaga pasa a colorear los polígonos.
 *
 *  Se puede elegir en tres niveles, y los tres son la misma regla —el lote toma
 *  el peor estado entre las plagas elegidas ({@link SeleccionPlagas})—:
 *
 *  - **"Todas"** (`null`), el estado de entrada: cada lote toma su peor estado
 *    venga de la plaga que venga. Es la pregunta que se le hace a un mapa
 *    —dónde hay problema— y no tiene el límite que sí tiene el dashboard, donde
 *    varias plagas a la vez impiden dibujar las bandas de umbral: un polígono
 *    tiene un color solo, salgan de una plaga o de nueve.
 *  - **Un grupo**, haciendo clic en su encabezado ("ÁCARO" elige sus tres):
 *    responde "cómo estoy de ácaros" sin obligar a mirarlos de a uno ni a
 *    armar la combinación a mano. Se eligió esto antes que casillas por plaga
 *    porque cubre el caso real con un clic y deja el panel igual de simple; si
 *    hiciera falta una combinación arbitraria, las casillas se suman encima sin
 *    tocar nada de lo de abajo (la selección ya es una lista).
 *  - **Una plaga**, que es el caso de un solo elemento.
 *
 *  Qué plaga manda, cuando hay más de una en juego, lo dicen la etiqueta del
 *  polígono y la fila del sidebar.
 *
 *  Al cambiar de semana, una plaga —o un grupo entero— que esa semana no se
 *  revisó **vuelve a "Todas"** en vez de saltar a otra plaga: cambiar de semana
 *  no debería cambiar también, por su cuenta, qué se está mirando.
 *
 *  No usa el selector del panel (`toolTipItems` → `TooltipRender`) porque ese
 *  monta un `<select>`: no puede mostrar el semáforo de cada plaga, ni agrupar,
 *  ni ofrecer el grupo como opción.
 *
 *  El acceso al informe **no** vive acá: está al fondo del sidebar. */
export class MonitoreoSesionPlagasBarRender {
  /** @param _onSeleccion Notifica las plagas activas al cambiar (`null` =
   *                      modo "Todas"). */
  constructor(
    private readonly _onSeleccion: (seleccion: SeleccionPlagas) => void,
  ) {
    BAR_ID += 1;
    this._id = BAR_ID;
  }

  /** Monta la barra dentro del panel superior del mapa. */
  mount(parent: HTMLElement): void {
    const contenedor = document.createElement("div");
    contenedor.className = "mon-ses-bar";
    contenedor.id = `monSesBar-${this._id}`;
    contenedor.innerHTML = `
      <button type="button" class="mon-ses-trigger" id="monSesTrigger-${this._id}">
        <span class="mon-ses-dot" id="monSesTriggerDot-${this._id}"></span>
        <span class="mon-ses-trigger-txt" id="monSesTriggerTxt-${this._id}">Todas las plagas</span>
        <i class="ph ph-caret-down mon-ses-trigger-caret"></i>
      </button>
      <div class="mon-ses-panel" id="monSesPanel-${this._id}" hidden>
        <div class="mon-ses-panel-head">
          <i class="ph ph-magnifying-glass"></i>
          <input class="mon-ses-search" id="monSesSearch-${this._id}" type="search"
                 placeholder="Buscar plaga…" autocomplete="off">
        </div>
        <div class="mon-ses-panel-list" id="monSesPills-${this._id}"></div>
      </div>`;
    parent.appendChild(contenedor);

    // Mismo trato que el resto de los controles del mapa
    // (`OrtomosaicoControl`, `MapZoomButton`…): sin esto el click de una pill
    // llega también al mapa y, en móvil —donde la barra pasa a scrollear en una
    // sola fila—, el arrastre horizontal haría pan del mapa en vez de correr
    // las pills. `disableScrollPropagation` evita además que la rueda sobre la
    // barra haga zoom.
    L.DomEvent.disableClickPropagation(contenedor);
    L.DomEvent.disableScrollPropagation(contenedor);

    this._contenedor = contenedor;
    this._pillsEl = document.getElementById(`monSesPills-${this._id}`);
    this._triggerEl = document.getElementById(`monSesTrigger-${this._id}`);
    this._panelEl = document.getElementById(`monSesPanel-${this._id}`);
    this._searchEl = document.getElementById(
      `monSesSearch-${this._id}`,
    ) as HTMLInputElement | null;

    this._pillsEl?.addEventListener("click", this._onPillClick);
    this._triggerEl?.addEventListener("click", this._onTriggerClick);
    this._searchEl?.addEventListener("input", this._onSearch);
    // Cerrar al hacer clic fuera o con Escape: el panel tapa el mapa y debe
    // quitarse de en medio sin obligar a apuntarle al disparador otra vez.
    document.addEventListener("click", this._onDocClick, true);
    document.addEventListener("keydown", this._onKeyDown);

    this._render();
  }

  dispose(): void {
    this._pillsEl?.removeEventListener("click", this._onPillClick);
    this._triggerEl?.removeEventListener("click", this._onTriggerClick);
    this._searchEl?.removeEventListener("input", this._onSearch);
    document.removeEventListener("click", this._onDocClick, true);
    document.removeEventListener("keydown", this._onKeyDown);
    this._contenedor?.remove();
    this._contenedor = null;
  }

  /** Catálogo de plagas (llega cuando la página termina de cargarlo). */
  setPlagas(plagas: MonitoreoPlagaEnfermedadItem[]): void {
    this._plagas = plagas;
    this._recalcular();
  }

  /** Cambia las incidencias mostradas —semana completa o un recorrido—:
   *  recalcula qué plagas se ofrecen y el estado del punto de cada una. */
  setDatos(datos: DatosMonitoreo | null): void {
    this._datos = datos;
    this._recalcular();
  }

  /** Id de instancia, para los ids del DOM. */
  private readonly _id: number;

  private _contenedor: HTMLElement | null = null;
  private _pillsEl: HTMLElement | null = null;
  private _triggerEl: HTMLElement | null = null;
  private _panelEl: HTMLElement | null = null;
  private _searchEl: HTMLInputElement | null = null;
  /** Panel desplegado. */
  private _abierto: boolean = false;
  /** Texto del buscador, normalizado. */
  private _busqueda: string = "";

  /** Catálogo completo. */
  private _plagas: MonitoreoPlagaEnfermedadItem[] = [];
  /** Incidencias activas. */
  private _datos: DatosMonitoreo | null = null;
  /** Plagas ofrecidas para la semana activa, en orden de catálogo. */
  private _disponibles: MonitoreoPlagaEnfermedadItem[] = [];
  /** Plaga activa, cuando se eligió una sola. */
  private _plagaId: string | null = null;
  /** Grupo activo, cuando se eligió un grupo entero. Excluyente con
   *  `_plagaId`: los dos en `null` es el modo "Todas". */
  private _grupo: string | null = null;

  /** Plagas activas, en el formato que consumen el mapa, el sidebar y la
   *  leyenda. Un grupo se resuelve contra lo DISPONIBLE esta semana, no contra
   *  el catálogo entero: así la selección nunca incluye plagas que no se
   *  revisaron. */
  private _getSeleccion(): SeleccionPlagas {
    if (this._plagaId) return [this._plagaId];
    if (this._grupo) return this._getPlagasDeGrupo(this._grupo).map((p) => p.id);
    return null;
  }

  /** Plagas disponibles de un grupo, en orden de catálogo. */
  private _getPlagasDeGrupo(grupo: string): MonitoreoPlagaEnfermedadItem[] {
    return this._disponibles.filter((plaga) => (plaga.grupo || "Otras") === grupo);
  }

  /** Recalcula las plagas disponibles y, si lo activo ya no aplica a esta
   *  semana, vuelve a "Todas". */
  private _recalcular(): void {
    this._disponibles = this._getDisponibles();

    // "Todas" siempre vale. Una plaga concreta solo si esta semana la reporta, y
    // un grupo solo si le queda alguna: si no, el mapa quedaría pintando algo
    // que no se revisó.
    const sigueValiendo = this._plagaId
      ? this._disponibles.some((plaga) => plaga.id === this._plagaId)
      : this._grupo
        ? this._getPlagasDeGrupo(this._grupo).length > 0
        : true;

    if (!sigueValiendo) {
      this._plagaId = null;
      this._grupo = null;
      this._onSeleccion(null);
    }

    this._render();
  }

  /** Peor estado de la semana entera, para el punto de la fila "Todas". */
  private _getPeorEstadoSemana(): MonitoreoEstado {
    return this._getPeorEstadoDe(this._disponibles);
  }

  /** Peor estado entre un conjunto de plagas: el punto de "Todas" y el de cada
   *  encabezado de grupo salen de acá. */
  private _getPeorEstadoDe(
    plagas: MonitoreoPlagaEnfermedadItem[],
  ): MonitoreoEstado {
    let peor: MonitoreoEstado = "ok";
    for (const plaga of plagas) {
      const estado = this._getPeorEstado(plaga);
      if (estado === "alert") return "alert";
      if (estado === "warn") peor = "warn";
    }
    return peor;
  }

  /** Plagas efectivamente monitoreadas en la semana: las que aparecen como
   *  clave en el `inc` de algún lote, con o sin detección. Mostrar todo el
   *  catálogo llenaría la barra de plagas que ni se revisaron.
   *
   *  Si la semana no reporta ninguna, se ofrece el catálogo entero para que la
   *  barra no quede vacía y se pueda elegir igual. */
  private _getDisponibles(): MonitoreoPlagaEnfermedadItem[] {
    if (!this._datos) return [];

    const enLaSemana = new Set<string>();
    for (const porPlaga of Object.values(this._datos)) {
      for (const plagaId of Object.keys(porPlaga)) enLaSemana.add(plagaId);
    }

    const filtradas = this._plagas.filter((plaga) => enLaSemana.has(plaga.id));
    return (filtradas.length > 0 ? filtradas : this._plagas).sort(
      (a, b) => a.orden - b.orden,
    );
  }

  /** Si la plaga registró alguna detección en la selección activa.
   *
   *  No es lo mismo que su estado: una plaga puede estar en "ok" y aun así
   *  haberse detectado por debajo del umbral. Lo que atenúa la pill es el cero
   *  absoluto — el caso en que elegirla no cambia nada en el mapa. */
  private _tieneDeteccion(plaga: MonitoreoPlagaEnfermedadItem): boolean {
    if (!this._datos) return false;
    for (const lote of Object.values(this._datos)) {
      if ((lote.inc[plaga.id] ?? 0) > 0) return true;
    }
    return false;
  }

  /** Peor estado de una plaga entre todos los lotes de la semana. */
  private _getPeorEstado(
    plaga: MonitoreoPlagaEnfermedadItem,
  ): MonitoreoEstado {
    let peor: MonitoreoEstado = "ok";
    if (!this._datos) return peor;

    for (const lote of Object.values(this._datos)) {
      if (lote.inc[plaga.id] === undefined) continue;
      const evaluacion = evaluarEnLote(lote, plaga.id, plaga);
      // Estado nulo es "no se puede evaluar" -benéfico o sin referencia-, y eso
      // no puede empeorar el color de la pill.
      if (!evaluacion || evaluacion.estado === null) continue;
      if (evaluacion.estado === "alert") return "alert";
      if (evaluacion.estado === "warn") peor = "warn";
    }
    return peor;
  }

  /**
   * Texto del umbral de alerta de una plaga, para el subtítulo de su pill.
   *
   * ⚠️ **La barra abarca todos los lotes de la semana, y el umbral ya no es uno
   * solo.** Se resuelve por (finca, cultivo, organismo), así que un lote de
   * limón y uno de naranja de la misma finca pueden tener umbrales distintos
   * para el mismo ácaro — que es exactamente para lo que existe la tabla por
   * cultivo. Antes esto leía `plaga.rangoMedio` del catálogo, que es un solo
   * número para los tres cítricos, y mostrarlo acá era dar por única una cifra
   * que no lo es.
   *
   * Así que: si todos los lotes que midieron esta plaga coinciden, se muestra el
   * número. Si no coinciden, se dice que depende del cultivo, en vez de elegir
   * uno al azar y hacerlo pasar por el de todos.
   */
  private _detalleUmbral(plaga: MonitoreoPlagaEnfermedadItem): string {
    if (!this._datos) return "";

    const distintos = new Set<string>();
    for (const lote of Object.values(this._datos)) {
      if (lote.inc[plaga.id] === undefined) continue;
      const evaluacion = evaluarEnLote(lote, plaga.id, plaga);
      if (!evaluacion) continue;
      const medio = lote.referencias[plaga.id]?.nivelMedioMax;
      if (medio !== undefined && medio > 0) {
        distintos.add(formatNivel({ ...evaluacion, nivel: medio }));
      }
    }

    if (distintos.size === 1) return `alerta desde ${[...distintos][0]}`;
    if (distintos.size > 1) return "alerta según el cultivo del lote";
    return plaga.isCritico ? "crítica" : "sin valores de referencia";
  }

  /** Normaliza para buscar: sin acentos y en minúsculas. */
  private _norm(texto: string): string {
    return texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  }

  /** Pinta el disparador (plaga activa) y la lista del panel, agrupada. */
  private _render(): void {
    this._renderTrigger();
    if (!this._pillsEl) return;

    if (this._disponibles.length === 0) {
      this._pillsEl.innerHTML = `<div class="mon-ses-vacio">Sin plagas para esta semana.</div>`;
      return;
    }

    const filtradas = this._busqueda
      ? this._disponibles.filter((plaga) =>
          this._norm(`${plaga.nombreComun} ${plaga.nombreTecnico} ${plaga.grupo ?? ""}`).includes(this._busqueda),
        )
      : this._disponibles;

    const partes: string[] = [];

    // "Todas" primero y fuera de los grupos: el mapa entra por acá, mostrando el
    // peor estado de cada lote venga de la plaga que venga. Es la pregunta que se
    // le hace al mapa; bajar a una plaga concreta viene después. No se filtra con
    // el buscador: siempre tiene que haber forma de volver.
    partes.push(this._filaHtml({
      id: "",
      nombre: "Todas las plagas",
      detalle: "el peor estado de cada lote",
      color: ESTADO_COLOR[this._getPeorEstadoSemana()],
      activa: this._plagaId === null && this._grupo === null,
      apagada: false,
    }));

    if (filtradas.length === 0) {
      partes.push(`<div class="mon-ses-vacio">Ninguna plaga coincide.</div>`);
    }

    // Se agrupa de verdad y no comparando con la plaga anterior: el catálogo
    // viene ordenado por `orden`, que intercala grupos, así que ese atajo
    // repetía los encabezados ("ÁCARO… HONGO… ÁCARO…"). El orden de los grupos
    // es el de su primera plaga, y dentro de cada uno se respeta `orden`.
    const porGrupo = new Map<string, MonitoreoPlagaEnfermedadItem[]>();
    for (const plaga of filtradas) {
      const grupo = plaga.grupo || "Otras";
      const lista = porGrupo.get(grupo);
      if (lista) lista.push(plaga);
      else porGrupo.set(grupo, [plaga]);
    }

    for (const [grupo, plagasDelGrupo] of porGrupo) {
      partes.push(this._grupoHtml(grupo));
      for (const plaga of plagasDelGrupo) {
      const sinDeteccion = !this._tieneDeteccion(plaga);
      partes.push(this._filaHtml({
        id: plaga.id,
        nombre: plaga.nombreComun,
        detalle: sinDeteccion ? "sin detección" : this._detalleUmbral(plaga),
        color: ESTADO_COLOR[this._getPeorEstado(plaga)],
        activa: plaga.id === this._plagaId,
        apagada: sinDeteccion,
      }));
      }
    }

    this._pillsEl.innerHTML = partes.join("");
  }

  /** Encabezado de un grupo, que además **es** la opción "todo el grupo".
   *
   *  El punto lleva el peor estado del grupo y el contador dice cuántas plagas
   *  elegiría. Ese contador sale de las disponibles de la semana y no de las
   *  que el buscador deja a la vista, porque el clic también elige el grupo
   *  completo: el buscador sirve para encontrar, no para redefinir qué es un
   *  grupo. No se nota porque el panel se cierra al elegir. */
  private _grupoHtml(grupo: string): string {
    const plagas = this._getPlagasDeGrupo(grupo);
    const activo = this._grupo === grupo;
    return `<button type="button" class="mon-ses-grupo${activo ? " mon-ses-grupo--active" : ""}" data-grupo="${grupo}">
      <span class="mon-ses-dot" style="background:${ESTADO_COLOR[this._getPeorEstadoDe(plagas)]}"></span>
      <span class="mon-ses-grupo-txt">${grupo}</span>
      <span class="mon-ses-grupo-n">${plagas.length === 1 ? "1 plaga" : `las ${plagas.length}`}</span>
    </button>`;
  }

  /** Una fila del panel. */
  private _filaHtml(fila: {
    id: string;
    nombre: string;
    detalle: string;
    color: string;
    activa: boolean;
    apagada: boolean;
  }): string {
    return `<button type="button" class="mon-ses-item${fila.activa ? " mon-ses-item--active" : ""}${fila.apagada ? " mon-ses-item--apagada" : ""}" data-plaga="${fila.id}">
      <span class="mon-ses-dot" style="background:${fila.color}"></span>
      <span class="mon-ses-item-nombre">${fila.nombre}</span>
      <span class="mon-ses-item-detalle">${fila.detalle}</span>
    </button>`;
  }

  /** El disparador muestra qué se está viendo, sin abrir el panel: la plaga, el
   *  grupo con cuántas plagas abarca, o "Todas". El punto, el peor estado de
   *  eso mismo. */
  private _renderTrigger(): void {
    const txt = document.getElementById(`monSesTriggerTxt-${this._id}`);
    const dot = document.getElementById(`monSesTriggerDot-${this._id}`);
    const activa = this._disponibles.find((plaga) => plaga.id === this._plagaId);
    const delGrupo = this._grupo ? this._getPlagasDeGrupo(this._grupo) : [];

    if (txt) {
      txt.textContent = activa
        ? activa.nombreComun
        : this._grupo
          ? `${this._grupo} · ${delGrupo.length} ${delGrupo.length === 1 ? "plaga" : "plagas"}`
          : "Todas las plagas";
    }
    if (dot) {
      dot.style.background = activa
        ? ESTADO_COLOR[this._getPeorEstado(activa)]
        : this._grupo
          ? ESTADO_COLOR[this._getPeorEstadoDe(delGrupo)]
          : ESTADO_COLOR[this._getPeorEstadoSemana()];
    }
  }

  private _toggle(abrir: boolean): void {
    this._abierto = abrir;
    if (this._panelEl) this._panelEl.hidden = !abrir;
    this._triggerEl?.classList.toggle("mon-ses-trigger--open", abrir);
    if (abrir) {
      // El buscador arranca limpio y enfocado: con 80 plagas, escribir es más
      // rápido que recorrer la lista.
      this._busqueda = "";
      if (this._searchEl) this._searchEl.value = "";
      this._render();
      this._searchEl?.focus();
    }
  }

  private _onTriggerClick = (): void => {
    this._toggle(!this._abierto);
  };

  private _onSearch = (): void => {
    this._busqueda = this._norm(this._searchEl?.value ?? "").trim();
    this._render();
  };

  private _onDocClick = (event: Event): void => {
    if (!this._abierto) return;
    if (this._contenedor?.contains(event.target as Node)) return;
    this._toggle(false);
  };

  private _onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && this._abierto) this._toggle(false);
  };

  private _onPillClick = (event: Event): void => {
    const opcion = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-plaga],[data-grupo]",
    );
    if (!opcion) return;

    // El encabezado del grupo trae `data-grupo`; las filas de plaga,
    // `data-plaga` (vacío = modo "Todas").
    const grupo = opcion.dataset.grupo ?? null;
    const plagaId = grupo ? null : opcion.dataset.plaga || null;

    this._toggle(false);
    if (plagaId === this._plagaId && grupo === this._grupo) return;

    this._plagaId = plagaId;
    this._grupo = grupo;
    this._render();
    this._onSeleccion(this._getSeleccion());
  };
}
